import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Music, ListMusic, ExternalLink, Search, Send, Check } from "lucide-react";

const SONGLIST_URL =
  "https://docs.google.com/spreadsheets/d/1_cnZe7jwptpuuhRnrXiOZJbxxFsUcXHt/edit?usp=sharing&ouid=103916521800636976298&rtpof=true&sd=true";

const MAX_RESULTS = 30;

const SongRequest = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [event, setEvent] = useState("");
  const [query, setQuery] = useState("");
  const [sending, setSending] = useState(null); // id of song being submitted
  const [sent, setSent] = useState(new Set()); // ids of successfully submitted songs
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ song_title: "", artist: "", message: "" });
  const nameRef = useRef(null);

  useEffect(() => {
    api.get("/songs")
      .then((r) => setSongs(r.data.songs || []))
      .catch(() => toast.error("Couldn't load the songlist"))
      .finally(() => setLoading(false));
  }, []);

  // Live-filtered results (case-insensitive, matches artist OR title OR both combined)
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const filtered = songs.filter((s) => {
      const hay = `${s.artist} ${s.title}`.toLowerCase();
      return hay.includes(q);
    });
    return filtered.slice(0, MAX_RESULTS);
  }, [songs, query]);

  const focusName = () => {
    nameRef.current?.focus();
    nameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const requestSong = async (song) => {
    if (!name.trim()) {
      toast.error("Enter your name first so the band knows who's asking");
      focusName();
      return;
    }
    const id = song.raw;
    setSending(id);
    try {
      await api.post("/song-requests", {
        name: name.trim(),
        song_title: song.title,
        artist: song.artist,
        event: event.trim() || undefined,
      });
      setSent((prev) => new Set(prev).add(id));
      toast.success(`Requested: ${song.title}`);
    } catch (err) {
      toast.error("Couldn't send that one. Try again.");
    } finally {
      setSending(null);
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!name.trim() || !manual.song_title.trim() || !manual.artist.trim()) {
      toast.error("Name, song title and artist are required");
      return;
    }
    setSending("manual");
    try {
      await api.post("/song-requests", {
        name: name.trim(),
        song_title: manual.song_title.trim(),
        artist: manual.artist.trim(),
        event: event.trim() || undefined,
        message: manual.message.trim() || undefined,
      });
      toast.success("Request sent! We'll do our best.");
      setManual({ song_title: "", artist: "", message: "" });
      setShowManual(false);
    } catch {
      toast.error("Couldn't send your request");
    } finally {
      setSending(null);
    }
  };

  const nameFilled = name.trim().length > 0;

  return (
    <div data-testid="song-request-page" className="min-h-screen">
      <section className="pt-20 pb-12 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <p className="eyebrow fade-up"><Music size={12} className="inline mr-2" /> What do you wanna hear?</p>
              <h1 className="font-heading text-6xl md:text-8xl uppercase leading-[0.9] mt-5 fade-up-1" data-testid="request-title">
                Request a Song
              </h1>
            </div>
            <a
              href={SONGLIST_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-retro fade-up-1 self-start md:self-center"
              data-testid="request-view-songlist"
            >
              <ListMusic size={14} className="mr-2" /> Full Songlist <ExternalLink size={12} className="ml-2" />
            </a>
          </div>
          <p className="mt-6 max-w-2xl text-lg text-neutral-300 fade-up-2">
            Search below. Tap a song and it goes straight to the band. We can't promise it'll happen tonight, but we'll do our best.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          {/* Your info */}
          <div className="grid md:grid-cols-2 gap-8 pb-10 mb-8 border-b border-white/10">
            <div>
              <label className="label-retro">Your name *</label>
              <input
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="field"
                placeholder="Jamie from the back"
                data-testid="sr-name"
              />
            </div>
            <div>
              <label className="label-retro">Event / show (optional)</label>
              <input
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                className="field"
                placeholder="Patrick Molloy's, Saturday"
                data-testid="sr-event"
              />
            </div>
          </div>

          {/* Search */}
          <div>
            <label className="label-retro">Find a song</label>
            <div className="relative">
              <Search size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="field pl-8"
                placeholder={loading ? "Loading songlist..." : `Search ${songs.length} songs by artist or title`}
                disabled={loading}
                data-testid="sr-search"
              />
            </div>
            {!nameFilled && (
              <p className="mt-3 font-mono text-[10px] text-yellow-300 tracking-[0.2em] uppercase" data-testid="sr-name-hint">
                → Enter your name above so tapping a song sends it straight through
              </p>
            )}
          </div>

          {/* Results */}
          {query.trim() && (
            <ul className="mt-6 divide-y divide-white/10 border-y border-white/10" data-testid="sr-results">
              {results.length === 0 && (
                <li className="py-6 text-center" data-testid="sr-no-results">
                  <p className="font-mono text-sm text-neutral-400">No matches for "{query}"</p>
                  <button
                    type="button"
                    onClick={() => setShowManual(true)}
                    className="mt-3 font-mono text-xs tracking-[0.2em] uppercase underline text-white"
                    data-testid="sr-request-anyway"
                  >
                    Request it anyway →
                  </button>
                </li>
              )}
              {results.map((s) => {
                const id = s.raw;
                const isSending = sending === id;
                const isSent = sent.has(id);
                return (
                  <li key={id} data-testid={`sr-result-${id}`}>
                    <button
                      type="button"
                      onClick={() => requestSong(s)}
                      disabled={isSending || isSent}
                      className="w-full text-left py-4 px-3 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors disabled:opacity-70"
                      data-testid={`sr-pick-${id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-heading text-lg md:text-xl leading-tight truncate">{s.title}</p>
                        <p className="text-xs md:text-sm text-neutral-400 font-mono truncate">{s.artist}</p>
                      </div>
                      <span
                        className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 border font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                          isSent
                            ? "bg-yellow-300 border-yellow-300 text-black"
                            : "border-white/40 group-hover:border-white text-white"
                        }`}
                      >
                        {isSent ? (<><Check size={12} /> Sent</>) : isSending ? "Sending..." : (<><Send size={12} /> Request</>)}
                      </span>
                    </button>
                  </li>
                );
              })}
              {songs.length > MAX_RESULTS && results.length === MAX_RESULTS && (
                <li className="py-4 text-center font-mono text-[10px] text-neutral-500 tracking-[0.2em] uppercase">
                  Showing top {MAX_RESULTS} · keep typing to narrow it down
                </li>
              )}
            </ul>
          )}

          {/* Empty state (no search yet) */}
          {!query.trim() && !loading && (
            <div className="mt-8 border border-white/15 p-8 text-center" data-testid="sr-empty-state">
              <ListMusic className="mx-auto mb-4 text-white/40" size={32} />
              <p className="font-heading text-2xl uppercase leading-tight">{songs.length} songs in the book</p>
              <p className="mt-3 text-sm text-neutral-400">Start typing above to search by artist or song title.</p>
            </div>
          )}

          {/* Manual (fallback for songs not in the list) */}
          <div className="mt-14 pt-8 border-t border-white/10">
            {!showManual ? (
              <div className="text-center">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-neutral-500">
                  Don't see your song?
                </p>
                <button
                  type="button"
                  onClick={() => setShowManual(true)}
                  className="mt-3 btn-retro !py-3 !px-5 !text-[10px]"
                  data-testid="sr-toggle-manual"
                >
                  Request it anyway
                </button>
              </div>
            ) : (
              <form onSubmit={submitManual} className="space-y-6" data-testid="sr-manual-form">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-2xl uppercase">Request a song not on the list</h2>
                  <button type="button" onClick={() => setShowManual(false)} className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400 hover:text-white">Cancel</button>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="label-retro">Song title *</label>
                    <input value={manual.song_title} onChange={(e) => setManual({ ...manual, song_title: e.target.value })} className="field" required data-testid="sr-manual-song" />
                  </div>
                  <div>
                    <label className="label-retro">Artist *</label>
                    <input value={manual.artist} onChange={(e) => setManual({ ...manual, artist: e.target.value })} className="field" required data-testid="sr-manual-artist" />
                  </div>
                </div>
                <div>
                  <label className="label-retro">Message to the band (optional)</label>
                  <textarea value={manual.message} onChange={(e) => setManual({ ...manual, message: e.target.value })} className="field-area" placeholder="It's my birthday / engagement / first dance..." data-testid="sr-manual-message" />
                </div>
                <div className="flex justify-end">
                  <button disabled={sending === "manual"} type="submit" className="btn-retro-solid disabled:opacity-50" data-testid="sr-manual-submit">
                    {sending === "manual" ? "Sending..." : "Send Request"}
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="mt-10 font-mono text-[10px] text-neutral-600 tracking-[0.2em] uppercase text-center">
            Requests aren't guaranteed but we read every one. Tip the band to bribe us. (Just kidding. Mostly.)
          </p>
        </div>
      </section>
    </div>
  );
};

export default SongRequest;
